// socketManager.ts - Socket.IO connection management for chunked uploaders
import { io, Socket } from 'socket.io-client';
import { getSocketUrl } from '../../../../config/environment';
import { getSession } from '../../../auth/supabase-auth';

export interface SocketConfig {
  transports?: ('websocket' | 'polling')[];
  timeout?: number;
  reconnection?: boolean;
  reconnectionDelay?: number;
  reconnectionDelayMax?: number;
  reconnectionAttempts?: number;
}

export type ProgressHandler = (data: Record<string, unknown>) => void;

const DEFAULT_CONFIG: SocketConfig = {
  transports: ['websocket', 'polling'],
  timeout: 300000, // 5 minutes
  reconnection: true,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 5000,
  reconnectionAttempts: 5
};

/**
 * Socket.IO connection manager for upload progress tracking
 * Provides centralized socket management with reconnection logic
 */
export class SocketManager {
  private socket: Socket | null = null;
  private currentUploadId: string | null = null;
  private config: SocketConfig;

  constructor(config: SocketConfig = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Initialize Socket.IO connection
   */
  initialize(socketUrl?: string): Socket {
    if (!this.socket) {
      const url = socketUrl || getSocketUrl();

      this.socket = io(url, {
        transports: this.config.transports,
        // Send the Supabase JWT on every (re)connect so the server can
        // authenticate the connection. Function form => a fresh token is
        // fetched each connect attempt (handles token refresh/reconnects).
        auth: (cb: (data: { token: string }) => void) => {
          getSession()
            .then((s) => cb({ token: s?.access_token ?? '' }))
            .catch(() => cb({ token: '' }));
        },
        autoConnect: true,
        reconnection: this.config.reconnection,
        reconnectionAttempts: this.config.reconnectionAttempts,
        reconnectionDelay: this.config.reconnectionDelay,
        reconnectionDelayMax: this.config.reconnectionDelayMax,
        timeout: this.config.timeout
      });

      // Setup reconnection handlers
      this.socket.on('disconnect', (reason) => {
        // Reconnect for ALL reasons except client-initiated disconnect
        if (reason !== 'io client disconnect') {
          this.socket?.connect();
        }
      });

      // Rejoin room after reconnect
      this.socket.on('connect', () => {
        if (this.currentUploadId) {
          this.socket?.emit('join', { uploadId: this.currentUploadId });
        }
      });
    }

    return this.socket;
  }

  /**
   * Get the current socket instance (creates one if not exists)
   */
  getSocket(): Socket {
    return this.initialize();
  }

  /**
   * Join a room for receiving upload progress updates
   */
  joinRoom(uploadId: string): void {
    this.currentUploadId = uploadId;
    const socket = this.getSocket();

    if (socket.connected) {
      socket.emit('join', { uploadId });
    } else {
      socket.once('connect', () => {
        socket.emit('join', { uploadId });
      });
    }
  }

  /**
   * Leave a room when upload is complete
   */
  leaveRoom(uploadId: string): void {
    if (this.socket) {
      try {
        this.socket.emit('leave', { uploadId });
        if (this.currentUploadId === uploadId) {
          this.currentUploadId = null;
        }
      } catch {
        // Ignore socket leave errors
      }
    }
  }

  /**
   * Subscribe to progress events
   */
  onProgress(handler: ProgressHandler): void {
    const socket = this.getSocket();
    socket.on('processing_progress', handler);
  }

  /**
   * Unsubscribe from progress events
   */
  offProgress(handler: ProgressHandler): void {
    if (this.socket) {
      this.socket.off('processing_progress', handler);
    }
  }

  /**
   * Subscribe to any socket event
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  on(event: string, handler: (...args: any[]) => void): void {
    const socket = this.getSocket();
    socket.on(event, handler);
  }

  /**
   * Unsubscribe from any socket event
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  off(event: string, handler?: (...args: any[]) => void): void {
    if (this.socket) {
      if (handler) {
        this.socket.off(event, handler);
      } else {
        this.socket.off(event);
      }
    }
  }

  /**
   * Emit an event
   */
  emit(event: string, data: unknown): void {
    if (this.socket) {
      this.socket.emit(event, data);
    }
  }

  /**
   * Check if socket is connected
   */
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * Disconnect and cleanup socket connection
   */
  disconnect(uploadId?: string): void {
    try {
      if (this.socket) {
        // Leave room if uploadId provided
        if (uploadId) {
          this.socket.emit('leave', { uploadId });
        }

        // Remove all listeners to prevent errors
        this.socket.removeAllListeners();
        this.socket.disconnect();
        this.socket = null;
        this.currentUploadId = null;
      }
    } catch {
      // Ignore socket disconnect errors
      this.socket = null;
      this.currentUploadId = null;
    }
  }
}

/**
 * Create a new socket manager instance (for isolated connections)
 */
export function createSocketManager(config?: SocketConfig): SocketManager {
  return new SocketManager(config);
}
